import * as amplitude from "@amplitude/analytics-browser";
import { useExpandHeight } from "@redotech/react-animation/transition";
import { useHover } from "@redotech/react-util/hover";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import LockIcon from "@redotech/redo-icons/lock-01.svg";
import TriangleDown from "@redotech/redo-icons/triangle-down_filled.svg";
import { filterTruthy } from "@redotech/util/array";
import { AllOrNone } from "@redotech/util/type";
import classNames from "classnames";
import {
  JSXElementConstructor,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMatch } from "react-router-dom";
import { CommandPaletteOpenContext } from "../../../command-palette/contexts/command-palette-context";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { TextWeightValue } from "../../../theme/typography";
import { OverflowTooltip } from "../../../tooltip/overflow-tooltip";
import { useTreeNode } from "../../../tree/tree-context";
import { RedoBadge } from "../../badge/redo-badge";
import { RedoIcon } from "../../icon/redo-icon";
import { NavIconSwap } from "../common/redo-nav-icon-swap";
import * as navigationCss from "../redo-navigation.module.css";
import { formatNotificationCount } from "./format-notification-count";
import { NavSectionIdContext } from "./redo-secondary-nav";

export type SecondaryNavItemProps = {
  friendlyName: string;
  id: string;
  notificationCount?: number;
  beta?: boolean;
  isNew?: boolean;
  isPrivate?: boolean;
  action:
    | { type: "link"; href: string }
    | { type: "button"; onClick?: () => void };
  dragHandleProps?: object;
  /** Override the active/selected state instead of deriving it from the URL path. */
  selected?: boolean;
  isFolder?: boolean;
  /**
   * When true, the item is hidden from view but still registers with the tree
   * context for global search. Use this instead of conditional rendering with &&.
   */
  hidden?: boolean;
  /**
   * When true, the notification badge will always use the black/outline style
   * regardless of the count (no red "attention" indicator).
   */
  muted?: boolean;
  /**
   * When true, dims the entire row (opacity 0.5) to signal the item is gated
   * or otherwise inaccessible. Used together with `isPrivate` to render the
   * full "locked feature" visual in the Conversion Cloud sidebar.
   */
  dimmed?: boolean;
} & AllOrNone<{
  UnfilledIcon: JSXElementConstructor<any>;
  FilledIcon: JSXElementConstructor<any>;
}> &
  AllOrNone<{ anyChildSelected: boolean; children?: ReactNode[] | ReactNode }>;

export interface NavItem {
  friendlyName: string;
  url?: string;
  Icon?: JSXElementConstructor<any>;
}

function navItemEquals(a: NavItem, b: NavItem): boolean {
  return (
    a.friendlyName === b.friendlyName && a.url === b.url && a.Icon === b.Icon
  );
}

export function useNavTreeNode(
  item: NavItem,
  { skipRegistration }: { skipRegistration?: boolean } = {},
) {
  return useTreeNode<NavItem>({
    data: item,
    dataEquals: navItemEquals,
    skipRegistration,
  });
}

export const RedoSecondaryNavItem = memo(function RedoSecondaryNavItem({
  friendlyName,
  action,
  notificationCount,
  beta,
  isNew,
  id,
  UnfilledIcon,
  FilledIcon,
  isPrivate,
  children,
  anyChildSelected,
  dragHandleProps,
  selected,
  isFolder = false,
  hidden = false,
  muted,
  dimmed,
}: SecondaryNavItemProps) {
  const sectionId = useContext(NavSectionIdContext);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [itemRef, setItemRef] = useState<HTMLDivElement | null>(null);
  const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);

  const navItem = useMemo(
    () => ({
      friendlyName,
      Icon: UnfilledIcon,
      url: action.type === "link" ? action.href : undefined,
    }),
    [friendlyName, UnfilledIcon, action],
  );

  const { id: nodeId, TreeContext } = useNavTreeNode(navItem);

  const hasChildren =
    !!children &&
    (!Array.isArray(children) || filterTruthy(children).length > 0);

  useEffect(() => {
    if (anyChildSelected) {
      setExpanded(true);
    }
  }, [anyChildSelected]);

  const path = action.type === "link" ? action.href : "";
  const selfMatch = useMatch({
    path: hasChildren ? path : path,
    caseSensitive: true,
    end: !hasChildren,
  });

  const hovered = useHover(itemRef);
  const isCommandPaletteOpen = useContext(CommandPaletteOpenContext);

  // When hidden, only render for tree registration when command palette is open
  // This prevents Playwright from finding hidden elements during normal operation
  if (hidden) {
    if (!isCommandPaletteOpen) {
      return null;
    }
    return (
      <TreeContext.Provider value={nodeId}>
        <div style={{ display: "none" }}>{children}</div>
      </TreeContext.Provider>
    );
  }

  const active =
    selected !== undefined ? selected : !!path && !!selfMatch && !hasChildren;

  const color: "primary" | "secondary" = active ? "primary" : "secondary";
  const textWeight: TextWeightValue = active ? "semibold" : "regular";

  const isDropdown = hasChildren;

  const as = action.type === "link" ? "a" : "button";
  const linkProps = action.type === "link" ? { to: action.href } : undefined;

  return (
    <Flex data-testid={id} dir="column" gap="none">
      <Flex
        as={as}
        className={classNames(
          navigationCss.navItem,
          !!active && navigationCss.active,
        )}
        data-testid={`secondary-nav-tab-${id}`}
        justify="space-between"
        linkProps={linkProps}
        onClick={() => {
          amplitude.track("nav/sidebar-item-clicked", {
            section: sectionId,
            item: id,
          });
          if (action.type === "button") {
            setExpanded(!expanded);
            action.onClick?.();
          }
        }}
        px="md"
        py="sm"
        radius="sm"
        ref={setItemRef}
        style={dimmed ? { opacity: 0.5 } : undefined}
      >
        <OverflowTooltip
          hideOnHover
          overflowRef={overflowRef}
          tooltipProps={{ title: friendlyName, darkMode: true }}
          wrapperStyle={{
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
          }}
        >
          <>
            {UnfilledIcon && (
              <NavIconSwap
                FilledIcon={FilledIcon}
                isSelected={active || (isFolder && expanded)}
                size={16}
                UnfilledIcon={UnfilledIcon}
              />
            )}
            <Text
              fontSize="xs"
              fontWeight={textWeight}
              overflow="hidden"
              ref={setOverflowRef}
              textColor={color}
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {friendlyName}
            </Text>
          </>
        </OverflowTooltip>
        {dragHandleProps && (
          <Flex
            align="center"
            className={classNames(
              hovered && navigationCss.show,
              navigationCss.dragHandle,
            )}
            {...dragHandleProps}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <RedoIcon color="tertiary" Icon={DotsReorderIcon} size={16} />
          </Flex>
        )}
        <Flex align="center" gap="xs">
          {notificationCount !== undefined && (
            <RedoBadge
              className={navigationCss.notificationCountBadge}
              color={muted || notificationCount === 0 ? "inactive" : "error"}
              darkMode
              size="xs"
              text={formatNotificationCount(notificationCount)}
              type="primary"
            />
          )}
          {beta && (
            <RedoBadge color="inactive" darkMode size="xs" text="Beta" />
          )}
          {isNew && <RedoBadge color="white" darkMode size="xs" text="New" />}
          {isPrivate && <RedoIcon color="tertiary" Icon={LockIcon} size={12} />}
          {isDropdown && (
            <Flex
              onClick={(e) => {
                e.preventDefault();
                setExpanded(!expanded);
              }}
            >
              <RedoIcon
                className={classNames(
                  navigationCss.dropdownIcon,
                  expanded && navigationCss.active,
                )}
                color="tertiary"
                Icon={TriangleDown}
                size={16}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      <TreeContext.Provider value={nodeId}>
        <RedoNavItemChildren expanded={expanded}>
          {children}
        </RedoNavItemChildren>
      </TreeContext.Provider>
    </Flex>
  );
});

const zeroHeight = { height: "0px", display: "none" };
const RedoNavItemChildren = memo(function RedoNavItemChildren({
  children,
  expanded,
}: {
  children: ReactNode[] | ReactNode | undefined;
  expanded: boolean;
}) {
  const [optionsContainer, setOptionsContainer] =
    useState<HTMLDivElement | null>(null);
  const [animateStyles, animating] = useExpandHeight(
    expanded,
    optionsContainer,
  );
  const isCommandPaletteOpen = useContext(CommandPaletteOpenContext);

  const hasChildren =
    !!children && (!Array.isArray(children) || children.length > 0);

  if (!hasChildren) {
    return null;
  }

  const style = !animating && !expanded ? zeroHeight : animateStyles;

  return (
    <>
      <Flex
        className={navigationCss.optionsContainer}
        dir="column"
        gap="xs"
        pl="3xl"
        pt={expanded ? "xs" : undefined}
        ref={setOptionsContainer}
        style={style}
      >
        {hasChildren && (animating || expanded) && children}
      </Flex>
      {/* Only render collapsed children for tree registration when command palette is open.
          This prevents Playwright from finding hidden elements during normal operation. */}
      {hasChildren && !animating && !expanded && isCommandPaletteOpen && (
        <div style={{ display: "none" }}>{children}</div>
      )}
    </>
  );
});
